import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Briefcase,
  Code2,
  CheckCircle2,
  ArrowRight,
  Server,
  Cloud,
  Shield,
  Zap,
  Database,
  Globe
} from 'lucide-react';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  enter: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

function ServicesPage() {
  const mainServices = [
    {
      icon: Briefcase,
      title: 'IT-rådgivning & Automatisering',
      description:
        'Vi hjælper jer med at finde de rigtige tekniske løsninger til jeres virksomhed. I fortæller os om jeres behov – vi designer og bygger løsningen.',
      features: [
        'Workflow-automatisering',
        'Procesoptimering',
        'Digital transformation',
        'Systemarkitektur & strategi',
        'Teknisk rådgivning',
        'Integrationer mellem systemer'
      ],
      color: 'blue'
    },
    {
      icon: Code2,
      title: 'Softwareudvikling',
      description:
        'Vi bygger og driver vores egne softwareprodukter. Fra idé til produktion udvikler vi systemer, der er designet til at fungere i virkelige virksomheder.',
      features: [
        'Webapplikationer',
        'API-udvikling & integration',
        'Cloud-løsninger',
        'Mobilapplikationer',
        'DevOps & automatiseret drift',
        'Modernisering af ældre systemer'
      ],
      color: 'cyan'
    }
  ];

  const capabilities = [
    {
      icon: Server,
      title: 'Backend-systemer',
      description: 'Skalerbare og vedligeholdelsesvenlige serverløsninger'
    },
    {
      icon: Globe,
      title: 'Webapplikationer',
      description: 'Moderne og brugervenlige webløsninger'
    },
    {
      icon: Cloud,
      title: 'Cloud-infrastruktur',
      description: 'Drift i AWS, Azure og Google Cloud'
    },
    {
      icon: Database,
      title: 'Data & integration',
      description: 'Effektive datapipelines og systemintegration'
    },
    {
      icon: Shield,
      title: 'Sikkerhed',
      description: 'Best practices for sikre systemer'
    },
    {
      icon: Zap,
      title: 'Ydeevne',
      description: 'Optimeret til hastighed og pålidelighed'
    }
  ];

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="enter"
      exit="exit"
      transition={{ duration: 0.4 }}
    >
      {/* Hero */}
      <section className="section" style={{ paddingTop: 'calc(var(--header-height) + var(--space-16))' }}>
        <div className="container">
          <motion.div
            className="section__header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="section__tag">Det vi gør</span>
            <h1 className="section__title">Vores ydelser</h1>
            <p className="section__description">
              Vi kombinerer IT-rådgivning med softwareudvikling.
              Ud over vores konsulentydelser designer og udvikler vi egne softwareløsninger, der løser reelle forretningsudfordringer.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section className="section section--alt">
        <div className="container">
          <div className="services-grid">
            {mainServices.map((service, index) => (
              <motion.article
                key={service.title}
                className="service-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className={`service-card__glow service-card__glow--${service.color}`} />
                
                <div className={`service-card__icon service-card__icon--${service.color}`}>
                  <service.icon />
                </div>

                <h2 className="service-card__title">{service.title}</h2>
                <p className="service-card__text">{service.description}</p>

                <ul className="service-card__list">
                  {service.features.map((feature) => (
                    <li key={feature} className="service-card__item">
                      <CheckCircle2 className="service-card__check" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="section">
        <div className="container">
          <motion.div
            className="section__header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="section__tag">Capabilities</span>
            <h2 className="section__title">Technical Expertise</h2>
            <p className="section__description">
              Our team brings deep experience across the full technology stack.
            </p>
          </motion.div>

          <div className="team-grid">
            {capabilities.map((cap, index) => (
              <motion.div
                key={cap.title}
                className="service-card"
                style={{ padding: 'var(--space-8)' }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <div className="service-card__icon service-card__icon--blue">
                  <cap.icon />
                </div>
                <h3 className="service-card__title" style={{ fontSize: 'var(--text-xl)' }}>
                  {cap.title}
                </h3>
                <p className="service-card__text">{cap.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section section--alt">
        <div className="container">
          <motion.div
            className="section__header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="section__title">Klar til at komme i gang??</h2>
            <p className="section__description">
              Lad os tage en snak om, hvordan vi kan hjælpe jeres virksomhed.
            </p>
            <div style={{ marginTop: 'var(--space-8)' }}>
              <Link to="/contact" className="btn btn--primary btn--large">
                Book en samtale
                <ArrowRight className="btn__icon" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}

export default ServicesPage;
